/**
 * Messages — unified in-app messaging (Iter120L).
 *
 * Left pane: conversation list + New-conversation button.
 * Right pane: message stream + composer.
 * Real-time: SSE (existing /api/events/stream) pushes `new_message` and
 * `typing` events.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import {
  Search, Send, Plus, MessagesSquare, Loader2, Users2, User as UserIcon,
  CheckCheck, Circle, Trash2, MessageCircle, X, Reply as ReplyIcon,
} from 'lucide-react';

const TOKEN_KEY = 'nexus_token';

const fmtRelTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

function api(path, opts = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }).then(async (r) => {
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || `Request failed (${r.status})`);
    }
    return r.json();
  });
}

// ============================================================
export default function Messages() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialConvo = searchParams.get('c') || null;
  const initialCase = searchParams.get('case') || null;

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(initialConvo);
  const [loading, setLoading] = useState(true);
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [self, setSelf] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem('nexus_user');
    try { setSelf(raw ? JSON.parse(raw) : null); } catch { /* ignore */ }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const r = await api('/api/messages/conversations');
      setConversations(r.conversations || []);
      // Auto-open case chat when arriving with ?case=<id>
      if (initialCase) {
        const found = (r.conversations || []).find(c => c.kind === 'case' && c.case_id === initialCase);
        if (found) setSelected(found.id);
        else {
          // Auto-create case chat
          try {
            const created = await api('/api/messages/conversations', {
              method: 'POST',
              body: JSON.stringify({ kind: 'case', case_id: initialCase }),
            });
            const c = created.conversation;
            setConversations(prev => [c, ...prev.filter(x => x.id !== c.id)]);
            setSelected(c.id);
          } catch (e) { toast({ title: 'Could not open case chat', description: e.message, variant: 'destructive' }); }
        }
      } else if (!selected && (r.conversations || []).length > 0) {
        setSelected(r.conversations[0].id);
      }
    } catch (e) {
      toast({ title: 'Could not load messages', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [initialCase, selected]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Live update via SSE (existing stream) — falls back to gentle polling.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    let es;
    try {
      es = new EventSource(`${BACKEND_URL}/api/events/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (ev) => {
        try {
          const parsed = JSON.parse(ev.data);
          if (parsed.type === 'new_message') {
            // Refresh convo list to update last_message + unread; keep selected as-is.
            setConversations(prev => {
              const conv_id = parsed.data?.conversation_id;
              const preview = parsed.data?.preview || '';
              const idx = prev.findIndex(c => c.id === conv_id);
              if (idx === -1) { loadConversations(); return prev; }
              const updated = { ...prev[idx],
                last_message_at: parsed.data?.message?.created_at || prev[idx].last_message_at,
                last_message_preview: preview,
                unread_count: (prev[idx].id === selected ? 0 : (prev[idx].unread_count || 0) + 1),
              };
              const list = [updated, ...prev.filter((_, i) => i !== idx)];
              return list;
            });
          }
        } catch { /* ignore malformed frames */ }
      };
    } catch (_) { /* fallback below */ }
    // Gentle polling every 20s as a safety net (SSE reconnect is best-effort)
    const iv = setInterval(loadConversations, 20000);
    return () => { if (es) es.close(); clearInterval(iv); };
  }, [selected, loadConversations]);

  const onConvoOpened = (id) => {
    setSelected(id);
    setSearchParams((prev) => { const p = new URLSearchParams(prev); p.set('c', id); return p; }, { replace: true });
    // Optimistic clear of unread badge
    setConversations(prev => prev.map(c => c.id === id ? { ...c, unread_count: 0 } : c));
  };

  const activeConvo = conversations.find(c => c.id === selected) || null;

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col md:flex-row gap-3" data-testid="messages-page">
      {/* Conversation list */}
      <aside className={`md:w-80 md:min-w-[18rem] md:max-w-[22rem] bg-[#0F172A] border border-slate-700 rounded-lg overflow-hidden flex flex-col ${selected ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-3 border-b border-slate-700 flex items-center justify-between gap-2">
          <div>
            <h1 className="text-white font-heading font-semibold text-lg flex items-center gap-1.5">
              <MessagesSquare className="w-4 h-4 text-amber-300" /> Messages
            </h1>
            <p className="text-[10px] text-slate-400">Secure in-app chat</p>
          </div>
          <Button size="sm" onClick={() => setNewModalOpen(true)}
            className="bg-amber-500 hover:bg-amber-400 !text-slate-900" data-testid="new-conversation-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Loading…</div>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">
              <MessagesSquare className="w-8 h-8 mx-auto text-slate-600 mb-2" />
              No conversations yet.<br />
              <button onClick={() => setNewModalOpen(true)} className="text-amber-400 underline mt-2">Start a conversation →</button>
            </div>
          ) : conversations.map(c => (
            <button
              key={c.id}
              onClick={() => onConvoOpened(c.id)}
              className={`w-full text-left p-3 border-b border-slate-800 hover:bg-slate-800/60 transition-colors ${selected === c.id ? 'bg-slate-800' : ''}`}
              data-testid={`conv-item-${c.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <ConvoAvatar convo={c} selfId={self?.id} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-white text-sm font-semibold truncate">{convoDisplayName(c, self?.id)}</span>
                      {c.kind === 'case' && <Badge className="bg-blue-500 text-white text-[9px] px-1">CASE</Badge>}
                      {c.kind === 'group' && <Badge className="bg-purple-500 text-white text-[9px] px-1">GROUP</Badge>}
                    </div>
                    <p className="text-xs text-slate-400 truncate">{c.last_message_preview || 'No messages yet'}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] text-slate-500">{fmtRelTime(c.last_message_at)}</div>
                  {c.unread_count > 0 && (
                    <div className="mt-1 inline-block bg-amber-500 text-slate-900 text-[10px] font-bold rounded-full px-1.5 py-0.5" data-testid={`unread-badge-${c.id}`}>
                      {c.unread_count}
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      {/* Message pane */}
      <main className={`flex-1 bg-[#0F172A] border border-slate-700 rounded-lg overflow-hidden flex flex-col ${selected ? 'flex' : 'hidden md:flex'}`}>
        {activeConvo ? (
          <ConversationPane
            key={activeConvo.id}
            convo={activeConvo}
            self={self}
            onBack={() => { setSelected(null); setSearchParams({}, { replace: true }); }}
            onDeleted={() => { setSelected(null); loadConversations(); }}
            onMessageSent={loadConversations}
          />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center">
            <MessagesSquare className="w-12 h-12 text-slate-600 mb-2" />
            <p className="text-sm">Select a conversation to start</p>
            <Button variant="outline" onClick={() => setNewModalOpen(true)}
              className="mt-4 border-slate-500 !text-slate-200">
              <Plus className="w-4 h-4 mr-1" /> New Conversation
            </Button>
          </div>
        )}
      </main>

      <NewConversationModal
        open={newModalOpen}
        onOpenChange={setNewModalOpen}
        onCreated={(c) => {
          setNewModalOpen(false);
          setConversations(prev => [c, ...prev.filter(x => x.id !== c.id)]);
          setSelected(c.id);
        }}
      />
    </div>
  );
}

// -------------------- helpers --------------------
function convoDisplayName(convo, selfId) {
  if (convo.kind === 'dm') {
    const other = (convo.members || []).find(m => m.id !== selfId);
    return other?.name || convo.name || 'Direct Message';
  }
  return convo.name || (convo.kind === 'case' ? 'Case Chat' : 'Group');
}

function ConvoAvatar({ convo, selfId }) {
  if (convo.kind === 'case') {
    return <div className="w-9 h-9 rounded-full bg-blue-500/20 border border-blue-400/40 flex items-center justify-center shrink-0">
      <Users2 className="w-4 h-4 text-blue-300" />
    </div>;
  }
  if (convo.kind === 'group') {
    return <div className="w-9 h-9 rounded-full bg-purple-500/20 border border-purple-400/40 flex items-center justify-center shrink-0">
      <Users2 className="w-4 h-4 text-purple-300" />
    </div>;
  }
  const other = (convo.members || []).find(m => m.id !== selfId);
  const initials = (other?.name || 'U').split(/\s+/).map(x => x[0]).slice(0, 2).join('').toUpperCase();
  return <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-200 font-bold text-xs shrink-0">
    {initials}
  </div>;
}

// -------------------- Conversation Pane --------------------
function ConversationPane({ convo, self, onBack, onDeleted, onMessageSent }) {
  const [messages, setMessages] = useState([]);
  const [reads, setReads] = useState([]);
  const [typing, setTyping] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const scrollRef = useRef(null);
  const typingTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api(`/api/messages/conversations/${convo.id}/messages`);
      setMessages(r.messages || []);
      setReads(r.reads || []);
      setTyping(r.typing || []);
      // Mark as read
      try { await api(`/api/messages/conversations/${convo.id}/read`, { method: 'POST' }); } catch { /* ignore */ }
    } catch (e) {
      toast({ title: 'Failed to load messages', description: e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [convo.id]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // SSE-driven refresh
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    let es;
    try {
      es = new EventSource(`${BACKEND_URL}/api/events/stream?token=${encodeURIComponent(token)}`);
      es.onmessage = (ev) => {
        try {
          const p = JSON.parse(ev.data);
          if (p.type === 'new_message' && p.data?.conversation_id === convo.id) {
            setMessages(prev => {
              if (prev.find(m => m.id === p.data.message.id)) return prev;
              return [...prev, p.data.message];
            });
            api(`/api/messages/conversations/${convo.id}/read`, { method: 'POST' }).catch(() => {});
          } else if (p.type === 'typing' && p.data?.conversation_id === convo.id) {
            setTyping(prev => {
              const filtered = prev.filter(t => t.user_id !== p.data.user_id);
              return [...filtered, { user_id: p.data.user_id, name: p.data.name, at: new Date().toISOString() }];
            });
            // Auto-clear typing after 6s
            setTimeout(() => setTyping(prev => prev.filter(t => t.user_id !== p.data.user_id)), 6000);
          }
        } catch { /* ignore */ }
      };
    } catch { /* SSE unsupported */ }
    return () => { if (es) es.close(); };
  }, [convo.id]);

  // Auto-scroll to bottom on new msgs
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const emitTyping = () => {
    if (typingTimer.current) clearTimeout(typingTimer.current);
    api(`/api/messages/conversations/${convo.id}/typing`, {
      method: 'POST', body: JSON.stringify({ typing: true }),
    }).catch(() => {});
    typingTimer.current = setTimeout(() => {
      api(`/api/messages/conversations/${convo.id}/typing`, {
        method: 'POST', body: JSON.stringify({ typing: false }),
      }).catch(() => {});
    }, 4000);
  };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const r = await api(`/api/messages/conversations/${convo.id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ body: text.trim(), reply_to_id: replyTo?.id || null }),
      });
      setMessages(prev => [...prev, r.message]);
      setText(''); setReplyTo(null);
      if (onMessageSent) onMessageSent();
    } catch (e) {
      toast({ title: 'Send failed', description: e.message, variant: 'destructive' });
    } finally { setSending(false); }
  };

  const del = async () => {
    if (!window.confirm('Remove this conversation from your inbox? Other participants will still see it.')) return;
    try {
      await api(`/api/messages/conversations/${convo.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) { toast({ title: 'Delete failed', description: e.message, variant: 'destructive' }); }
  };

  const displayName = convoDisplayName(convo, self?.id);
  const memberBadges = (convo.members || []).filter(m => m.id !== self?.id).slice(0, 5);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="p-3 border-b border-slate-700 flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button onClick={onBack} className="md:hidden text-slate-300 hover:text-white" data-testid="convo-back-btn">
            <X className="w-5 h-5" />
          </button>
          <ConvoAvatar convo={convo} selfId={self?.id} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-white font-semibold text-sm truncate">{displayName}</h2>
              {convo.kind === 'case' && <Badge className="bg-blue-500 text-white text-[9px]">CASE</Badge>}
              {convo.kind === 'group' && <Badge className="bg-purple-500 text-white text-[9px]">GROUP</Badge>}
            </div>
            <p className="text-[10px] text-slate-400 truncate">
              {memberBadges.map(m => m.name || m.username).join(' · ') || 'No other members'}
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={del}
          className="border-slate-600 !text-red-400 hover:bg-red-500/10 hover:!text-red-300"
          data-testid="delete-conv-btn">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Message stream */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {loading ? (
          <div className="text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-slate-500 text-sm mt-8">
            <MessageCircle className="w-8 h-8 mx-auto text-slate-600 mb-2" />
            No messages yet — say hello 👋
          </div>
        ) : messages.map(m => {
          const isMine = m.sender_id === self?.id;
          const repliedTo = m.reply_to_id ? messages.find(x => x.id === m.reply_to_id) : null;
          return (
            <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${m.id}`}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 ${isMine ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-white border border-slate-700'}`}>
                {!isMine && <div className="text-[10px] font-semibold text-amber-300 mb-0.5">{m.sender_name}</div>}
                {repliedTo && (
                  <div className={`text-[10px] pl-2 border-l-2 mb-1 ${isMine ? 'border-slate-900/40 text-slate-800' : 'border-amber-400/60 text-slate-300'}`}>
                    ↳ {repliedTo.sender_name}: {(repliedTo.body || '').slice(0, 60)}
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words text-sm">{m.body}</div>
                <div className={`text-[9px] mt-1 flex items-center gap-1 ${isMine ? 'text-slate-800/70 justify-end' : 'text-slate-400'}`}>
                  {fmtTime(m.created_at)}
                  {isMine && <CheckCheck className="w-3 h-3" />}
                  <button onClick={() => setReplyTo(m)} className="ml-2 opacity-60 hover:opacity-100" title="Reply">
                    <ReplyIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
        {typing.length > 0 && (
          <div className="text-[11px] text-slate-400 italic pl-2 flex items-center gap-2" data-testid="typing-indicator">
            <Circle className="w-2 h-2 fill-amber-400 text-amber-400 animate-pulse" />
            {typing.map(t => t.name || 'Someone').join(', ')} typing…
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="p-3 border-t border-slate-700 shrink-0">
        {replyTo && (
          <div className="mb-2 flex items-start gap-2 bg-slate-800 border border-slate-700 rounded p-2">
            <ReplyIcon className="w-3 h-3 text-amber-400 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-semibold text-amber-300">Replying to {replyTo.sender_name}</div>
              <div className="text-xs text-slate-400 truncate">{replyTo.body}</div>
            </div>
            <button onClick={() => setReplyTo(null)} className="text-slate-400 hover:text-white">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <Textarea
            value={text}
            onChange={(e) => { setText(e.target.value); emitTyping(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
            }}
            className="bg-slate-800 border-slate-600 text-white min-h-[42px] max-h-40 resize-none"
            placeholder="Write a message… (Enter to send, Shift+Enter for newline)"
            rows={1}
            data-testid="msg-composer-input"
          />
          <Button onClick={send} disabled={sending || !text.trim()}
            className="bg-amber-500 hover:bg-amber-400 !text-slate-900 shrink-0"
            data-testid="msg-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

// -------------------- New Conversation Modal --------------------
function NewConversationModal({ open, onOpenChange, onCreated }) {
  const [kind, setKind] = useState('dm');            // 'dm' | 'group'
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open) return;
    setKind('dm'); setSearchQ(''); setSearchResults([]);
    setSelectedUsers([]); setGroupName('');
    // preload directory
    (async () => {
      setSearching(true);
      try {
        const r = await api('/api/messages/users/search?q=');
        setSearchResults(r.users || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api(`/api/messages/users/search?q=${encodeURIComponent(searchQ)}`);
        setSearchResults(r.users || []);
      } catch { setSearchResults([]); }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ, open]);

  const toggleUser = (u) => {
    setSelectedUsers(prev => {
      const has = prev.find(x => x.id === u.id);
      if (has) return prev.filter(x => x.id !== u.id);
      if (kind === 'dm') return [u]; // Only one allowed
      return [...prev, u];
    });
  };

  const submit = async () => {
    if (selectedUsers.length === 0) {
      toast({ title: 'Pick at least one recipient', variant: 'destructive' });
      return;
    }
    if (kind === 'group' && selectedUsers.length < 2) {
      toast({ title: 'Group requires at least 2 other users', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const r = await api('/api/messages/conversations', {
        method: 'POST',
        body: JSON.stringify({
          kind, member_ids: selectedUsers.map(u => u.id),
          name: kind === 'group' ? (groupName.trim() || null) : null,
        }),
      });
      onCreated(r.conversation);
    } catch (e) { toast({ title: 'Could not create conversation', description: e.message, variant: 'destructive' }); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F172A] border-slate-700 text-white max-w-md" data-testid="new-conv-modal">
        <DialogHeader><DialogTitle className="!text-white">New Conversation</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            <button onClick={() => { setKind('dm'); setSelectedUsers([]); }}
              className={`flex-1 py-1.5 px-2 text-xs font-semibold border rounded ${kind === 'dm' ? 'bg-amber-500/20 border-amber-400 text-amber-200' : 'border-slate-600 text-slate-300'}`}
              data-testid="kind-dm-btn">
              Direct Message
            </button>
            <button onClick={() => { setKind('group'); setSelectedUsers([]); }}
              className={`flex-1 py-1.5 px-2 text-xs font-semibold border rounded ${kind === 'group' ? 'bg-amber-500/20 border-amber-400 text-amber-200' : 'border-slate-600 text-slate-300'}`}
              data-testid="kind-group-btn">
              Group Chat
            </button>
          </div>
          {kind === 'group' && (
            <div>
              <label className="text-xs text-slate-300">Group Name</label>
              <Input value={groupName} onChange={(e) => setGroupName(e.target.value)}
                className="bg-slate-800 border-slate-600 text-white" placeholder="Optional group name" />
            </div>
          )}
          <div>
            <label className="text-xs text-slate-300">Search users</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                className="pl-8 bg-slate-800 border-slate-600 text-white"
                placeholder="Name, username, role, department…"
                data-testid="user-search-input" />
            </div>
          </div>
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selectedUsers.map(u => (
                <button key={u.id} onClick={() => toggleUser(u)}
                  className="inline-flex items-center gap-1 bg-amber-500/20 text-amber-200 border border-amber-400/40 rounded-full px-2 py-0.5 text-[11px]"
                  data-testid={`selected-user-${u.id}`}>
                  {u.name || u.username}
                  <X className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}
          <div className="max-h-56 overflow-y-auto border border-slate-700 rounded bg-slate-900/60">
            {searching ? (
              <div className="p-3 text-center text-slate-400 text-xs"><Loader2 className="w-3 h-3 inline animate-spin mr-1" /> Searching…</div>
            ) : searchResults.length === 0 ? (
              <div className="p-3 text-center text-slate-500 text-xs">No users found</div>
            ) : searchResults.map(u => {
              const picked = selectedUsers.find(x => x.id === u.id);
              return (
                <button key={u.id} onClick={() => toggleUser(u)}
                  className={`w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-slate-800 ${picked ? 'bg-slate-800/60' : ''}`}
                  data-testid={`user-opt-${u.id}`}>
                  <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white truncate">{u.name || u.username}</div>
                    <div className="text-[10px] text-slate-400 truncate">{u.role_display || u.role} · {u.company_name || 'No company'}</div>
                  </div>
                  {picked && <span className="text-amber-400 text-[10px]">✓</span>}
                </button>
              );
            })}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}
              className="border-slate-600 !text-slate-200">Cancel</Button>
            <Button onClick={submit} disabled={creating || selectedUsers.length === 0}
              className="bg-amber-500 hover:bg-amber-400 !text-slate-900 disabled:opacity-60"
              data-testid="create-conv-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Start Conversation'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
